import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { FilterIcon, MinusIcon, PlusIcon } from "lucide-react";
import { useContext, useMemo } from "react";
import {
  Button,
  Disclosure,
  DisclosurePanel,
  DisclosureStateContext,
} from "react-aria-components";
import z from "zod";

import { button } from "@/components/base/button";
import { Checkbox } from "@/components/base/checkbox";
import { divisionsQueryOptions } from "@/data/divisions";
import { genderSchema } from "@/db/schema/shared";

import { FilterDateRange } from "./date-range";
import { FilterDivisions } from "./divisions";
import { FilterGender } from "./gender";
import { FilterName } from "./name";
import { FilterVenues } from "./venues";

export const tournamentListFilterSchema = z.object({
  page: z.number().default(1),
  pageSize: z.number().default(25),
  name: z.string().nullable().default(null),
  divisions: z.array(z.number()).default([]),
  venues: z.array(z.number()).default([]),
  genders: z.array(genderSchema).default([]),
  past: z.boolean().default(false),
  startDate: z.string().nullable().default(null),
  endDate: z.string().nullable().default(null),
});

export type TournamentListFiltersProps = z.infer<
  typeof tournamentListFilterSchema
>;

function getActiveFilterCount(props: TournamentListFiltersProps) {
  let count = 0;
  if (props.name) count++;
  if (props.divisions.length) count++;
  if (props.venues.length) count++;
  if (props.genders.length) count++;
  if (props.past) count++;
  if (props.startDate || props.endDate) count++;
  return count;
}

function FiltersTrigger({ activeCount }: { activeCount: number }) {
  const { isExpanded } = useContext(DisclosureStateContext)!;

  return (
    <Button
      slot="trigger"
      className="flex w-full cursor-default items-center justify-between py-2 text-base font-semibold text-gray-900 outline-hidden"
    >
      <span className="flex items-center gap-2">
        <FilterIcon size={16} />
        Filters
        {activeCount > 0 && (
          <span className="bg-navbar-background text-navbar-foreground rounded-full px-2 py-0.5 text-xs">
            {activeCount}
          </span>
        )}
      </span>
      {isExpanded ? (
        <MinusIcon size={18} className="text-gray-500" />
      ) : (
        <PlusIcon size={18} className="text-gray-500" />
      )}
    </Button>
  );
}

export function TournamentListFilters(props: TournamentListFiltersProps) {
  const { name, past, startDate, endDate, divisions, venues, genders } = props;
  const router = useRouter();
  const activeCount = getActiveFilterCount(props);

  const { data: allDivisions } = useSuspenseQuery(divisionsQueryOptions());

  const juniorDivisionIds = useMemo(
    () => allDivisions.filter((d) => d.maxAge != null).map((d) => d.id),
    [allDivisions],
  );

  const isTourStopActive = name === "Cal Cup Tour Stop";
  const isPremierActive = name === "Premier";
  const isJuniorsActive =
    juniorDivisionIds.length > 0 &&
    juniorDivisionIds.every((id) => divisions.includes(id)) &&
    divisions.length === juniorDivisionIds.length;

  return (
    <div className="mx-auto max-w-xl px-2">
      <Disclosure defaultExpanded>
        <FiltersTrigger activeCount={activeCount} />
        <DisclosurePanel className="h-(--disclosure-panel-height) overflow-clip">
          <div className="flex flex-col space-y-2 py-2">
            <FilterDateRange startDate={startDate} endDate={endDate} />
            <Checkbox
              isSelected={past}
              onChange={(value) => {
                router.navigate({
                  search: (prev) => ({
                    ...prev,
                    page: 1,
                    past: value,
                  }),
                });
              }}
              label={<>Past Tournaments Only</>}
            />
            <FilterVenues values={new Set(venues)} />
            <FilterDivisions values={new Set(divisions)} />
            <FilterGender values={new Set(genders)} />
            <FilterName value={name} />
            <div className="flex flex-row gap-2">
              <Link
                className={button({
                  variant: isTourStopActive ? "solid" : "outline",
                  size: "sm",
                })}
                search={(search) => ({
                  ...search,
                  page: 1,
                  name: isTourStopActive ? null : "Cal Cup Tour Stop",
                })}
              >
                Tour Stop
              </Link>
              <Link
                className={button({
                  variant: isPremierActive ? "solid" : "outline",
                  size: "sm",
                })}
                search={(search) => ({
                  ...search,
                  page: 1,
                  name: isPremierActive ? null : "Premier",
                })}
              >
                Premier
              </Link>
              <Link
                className={button({
                  variant: isJuniorsActive ? "solid" : "outline",
                  size: "sm",
                })}
                search={(search) => ({
                  ...search,
                  page: 1,
                  divisions: isJuniorsActive ? [] : juniorDivisionIds,
                })}
              >
                Juniors
              </Link>
            </div>
            <Link
              className={button({ class: "mt-2" })}
              search={{
                name: null,
                startDate: null,
                endDate: null,
              }}
            >
              Clear Filters
            </Link>
          </div>
        </DisclosurePanel>
      </Disclosure>
    </div>
  );
}
